import { useState, useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Button, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { Trash2Icon } from 'lucide-react';
import { useDetachDealersMutation } from '../hooks/useDetachDealersMutation';
import { AttachDealersModal } from './AttachDealersModal';
import type { DealerRef } from '../types/security-admin.types';

interface Props {
  groupId: string;
  ownerDealerId: string;
  dealers: DealerRef[];
}

export function SecurityGroupDealersPanel({ groupId, ownerDealerId, dealers }: Props) {
  const [showModal, setShowModal] = useState(false);
  const detachMutation = useDetachDealersMutation(groupId);

  const columns = useMemo<ColumnDef<DealerRef>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: (info) => {
        const dealer = info.row.original;
        // Icon-only Button — `Tooltip` is visual only, so the accessible
        // name comes from `ariaLabel`. Use the dealer name so screen-reader
        // users can tell rows apart.
        return (
          <div className="flex space-x-2">
            <PermissionGuard permission={{ resource: 'security_group', action: 'manage_dealers' }}>
              <Tooltip content="Detach dealer">
                <TextLink
                  onClick={() => detachMutation.mutate([dealer.id])}
                  color="error"
                  startIcon={<Trash2Icon size={18} />}
                  disabled={detachMutation.isPending}
                  ariaLabel={`Detach dealer ${dealer.name}`}
                />
              </Tooltip>
            </PermissionGuard>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [detachMutation.isPending]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Attached Dealers</h3>
        <PermissionGuard permission={{ resource: 'security_group', action: 'manage_dealers' }}>
          <Button size="sm" type="button" onClick={() => setShowModal(true)}>
            Attach Dealers
          </Button>
        </PermissionGuard>
      </div>

      {dealers.length === 0 ? (
        <p className="text-mp-muted text-sm">No dealers attached to this security group.</p>
      ) : (
        <Table<DealerRef> data={dealers} columns={columns} enableSorting={false} />
      )}

      {showModal && (
        <AttachDealersModal
          groupId={groupId}
          ownerDealerId={ownerDealerId}
          attachedDealerIds={dealers.map((d) => d.id)}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
